export default function registerGlobalSearch(Alpine) {
  Alpine.data("globalSearch", () => ({
    query: "",
    results: [],
    loading: false,
    open: false,
    selectedIndex: -1,
    abortController: null,

    init() {
      this._onKeydown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "k") {
          e.preventDefault();
          const desktopInput = document.getElementById("global-search");
          if (desktopInput && window.getComputedStyle(desktopInput.closest("[x-data]")).display !== "none") {
            desktopInput.focus();
            desktopInput.select();
          } else {
            this.$dispatch("open-modal", "search-modal");
          }
        }
      };
      document.addEventListener("keydown", this._onKeydown);
    },

    destroy() {
      document.removeEventListener("keydown", this._onKeydown);
    },

    get groupedResults() {
      const order = ["customer", "contact", "deal", "contract", "product"];
      const labels = {
        customer: "Customers",
        contact: "Contacts",
        deal: "Deals",
        contract: "Contracts",
        product: "Products"
      };

      let globalIndex = 0;
      return order
        .map((type) => {
          const items = this.results.filter((r) => r.type === type).map((r) => ({ ...r, _globalIndex: globalIndex++ }));
          return { type, label: labels[type] || type, items };
        })
        .filter((g) => g.items.length > 0);
    },

    async search() {
      if (this.query.length < 2) {
        this.results = [];
        this.open = false;
        return;
      }

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      this.loading = true;
      this.open = true;

      try {
        const response = await fetch(`/search?q=${encodeURIComponent(this.query)}`, {
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          signal: this.abortController.signal
        });
        const data = await response.json();
        this.results = data.results;
        this.selectedIndex = -1;
      } catch (e) {
        if (e.name !== "AbortError") console.error("Search failed:", e);
      } finally {
        this.loading = false;
      }
    },

    moveDown() {
      if (this.selectedIndex < this.results.length - 1) {
        this.selectedIndex++;
      }
    },

    moveUp() {
      if (this.selectedIndex > 0) {
        this.selectedIndex--;
      }
    },

    goToSelected() {
      if (this.selectedIndex >= 0 && this.selectedIndex < this.results.length) {
        const result = this.results[this.selectedIndex];
        if (result) window.location.href = result.url;
      }
    },

    typeStyle(type) {
      const styles = {
        customer: {
          bg: "bg-blue-100 dark:bg-blue-900/30",
          text: "text-blue-600 dark:text-blue-400",
          icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21m-3.75 3H21" /></svg>'
        },
        contact: {
          bg: "bg-emerald-100 dark:bg-emerald-900/30",
          text: "text-emerald-600 dark:text-emerald-400",
          icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" /></svg>'
        },
        deal: {
          bg: "bg-amber-100 dark:bg-amber-900/30",
          text: "text-amber-600 dark:text-amber-400",
          icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>'
        },
        contract: {
          bg: "bg-purple-100 dark:bg-purple-900/30",
          text: "text-purple-600 dark:text-purple-400",
          icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" /></svg>'
        },
        product: {
          bg: "bg-rose-100 dark:bg-rose-900/30",
          text: "text-rose-600 dark:text-rose-400",
          icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m21 7.5-9-5.25L3 7.5m18 0-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" /></svg>'
        }
      };
      return styles[type] || styles.customer;
    }
  }));
}
